import styled from 'styled-components';
import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { setShowBuilder } from 'actions/search';
import { Form, Button, Icon, SearchButton } from 'components/common';
import { ActionButtons, SearchAreaContainer } from './styled';
import { getShowBuilder } from 'selectors/search';
import SearchBuilder from '../../Search/SearchArea/SearchBuilder';
import OrgFilterField from '../Filters/OrgFilterField';
import {
  clearFilters,
  setFilterGroup,
  setWorkItemCategoryFilterGroup,
} from 'actions/organisation';
import {
  getFilter,
  getWorkItemCategoryFilter,
  getHasValidQuery,
  SLICE,
  getWorkItemCategories,
} from 'selectors/organisation';

import { useAuth, useDefaultSearchCategories } from 'hooks';

import licenseType from 'models/licenseType';
import { performBoolSearch, performPhraseSearch } from 'selectors/userSettings';
import BoolErrorMessage from 'components/Search/BoolErrorMessage';
import { SearchDiv } from 'components/Item/styled';
import { NLPSearchOpenButton } from 'components/SearchNLP/NLPSearchOpenButton';
import { aiSearchEmptyOrgTooltip } from 'utils/constants';

// Wraps onto a new line rather than squeezing the query input below its placeholder width.
export const SearchBar = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  row-gap: 10px;
  margin-top: 10px;
  > ${SearchDiv} {
    min-width: 300px;
  }
`;

const CategoryCheckboxes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
`;

/**
 * The org profile's search bar, shared by the People and Activity tabs (SCIL-19200).
 * The tabs differ only in which slice reports their in-flight search, the Search
 * Builder's filter group and the SearchButton's location tag.
 * @param {object} props
 * @param {(orgId: number) => (state: object) => boolean} props.loadingSelector
 * @param {string} [props.filterGroup] Search Builder filter group; omitted for People.
 * @param {string} props.location SearchButton location tag.
 */
export const OrgSearchArea = ({
  organisation,
  handleSearch,
  handleCancel,
  loading,
  loadingSelector,
  filterGroup,
  location,
}) => {
  const dispatch = useDispatch();
  const showBuilder = useSelector(getShowBuilder);

  const [showBoolError, setShowBoolError] = useState(false);
  const getHasBoolError = useSelector(
    performBoolSearch(SLICE, organisation.id)
  );
  const getHasPhraseError = useSelector(
    performPhraseSearch(SLICE, organisation.id)
  );
  const isDataloading = useSelector(loadingSelector(organisation.id));
  const isLoading = isDataloading || loading;
  const searchTermRef = useRef();

  const categoryFilters = useSelector(getWorkItemCategories(organisation.id));
  const setCategoryFilters = useCallback(
    categories => {
      dispatch(
        setFilterGroup(organisation.id, 'workItemCategories', categories)
      );
    },
    [dispatch, organisation.id]
  );
  const { resetSearchCategoriesToDefault } = useDefaultSearchCategories(
    categoryFilters,
    setCategoryFilters,
    isDataloading
  );

  const handleClear = () => {
    dispatch(clearFilters(organisation.id));
    // Clearing empties the categories, and an absent selection reads as every
    // category on. Put the user's default back, the way main search does.
    resetSearchCategoriesToDefault();
    handleSearch();
  };

  const handleSearchBuilderClear = () => {
    dispatch(setFilterGroup(organisation.id, 'term', ''));
  };

  const handleSearchBuilderSearch = value => {
    dispatch(setFilterGroup(organisation.id, 'term', value));
    handleSearch();
  };

  const updateSearch = value => {
    dispatch(setFilterGroup(organisation.id, 'term', value));
  };

  const handleShowBuilder = () => {
    dispatch(setShowBuilder(true));
  };

  const handleLocalSearch = () => {
    if (showBoolError !== null && (getHasBoolError || getHasPhraseError))
      setShowBoolError(true);
    else {
      handleSearch();
    }
  };

  const { hasLicense, user } = useAuth();
  const hasIndustryLicense = hasLicense([licenseType.industry], true);

  const hasValidQuery = useSelector(getHasValidQuery(organisation.id));

  // Org-wide totals from the profile payload, not the current search results.
  // Undefined counts (payload predates them) leave the button enabled.
  const isOrgEmpty =
    organisation.totalResearchers === 0 && organisation.totalActivities === 0;

  return (
    organisation && (
      <SearchAreaContainer>
        <SearchBar>
          <Button
            style={{ marginRight: 10 }}
            type='secondary'
            onClick={handleShowBuilder}
            disabled={isLoading}
          >
            <Icon icon='magnifyingGlassPlus' color='#8D8D8D' size='sm' />
            <span style={{ marginLeft: '5px' }}>Open Search Builder</span>
          </Button>
          {user?.hasSciSearchAI && (
            <NLPSearchOpenButton
              loading={isLoading}
              size='default'
              style={{ marginRight: 10 }}
              disabled={isOrgEmpty}
              tooltip={isOrgEmpty ? aiSearchEmptyOrgTooltip : undefined}
            />
          )}
          <SearchDiv ref={searchTermRef}>
            <Form width='100%' onSubmit={handleLocalSearch} style={{ flex: 1 }}>
              <OrgFilterField
                placeholder='(NGS OR "Next generation sequencing") AND CRISPR'
                filterId='term'
                organisationId={organisation.id}
                filterType='input'
                data-testid='org-filter-term'
                size='default'
                setFilterGroup={setFilterGroup}
                getFilter={getFilter}
                disabled={isLoading}
              />
            </Form>
            {showBoolError ? (
              <BoolErrorMessage
                updateSearchQuery={value => updateSearch(value)}
                performSearch={handleSearch}
                hasBoolFailed={getHasBoolError}
                term={getFilter(organisation.id, 'term')}
                hasParseFailed={getHasPhraseError}
                searchTermRef={searchTermRef}
                setShowBoolError={setShowBoolError}
              />
            ) : (
              <></>
            )}
          </SearchDiv>
          <ActionButtons>
            <Button
              type='secondary'
              onClick={handleClear}
              data-testid='clear-search-button'
              disabled={isLoading}
            >
              Clear
            </Button>
            <SearchButton
              type='primary'
              data-testid='search-button'
              loading={isLoading}
              disabled={!hasValidQuery || isLoading}
              onCancel={handleCancel}
              onSearch={handleLocalSearch}
              location={location}
            />
          </ActionButtons>
        </SearchBar>
        <CategoryCheckboxes>
          <span style={{ marginRight: '10px', lineHeight: '30px' }}>
            Search across:
          </span>
          <OrgFilterField
            helpText='Funding'
            icon='award'
            filterType='category'
            filterId='funding'
            organisationId={organisation.id}
            setFilterGroup={setWorkItemCategoryFilterGroup}
            getFilter={getWorkItemCategoryFilter}
            disabled={isLoading}
          >
            Funding
          </OrgFilterField>
          <OrgFilterField
            helpText='Tradeshows'
            icon='image'
            filterType='category'
            filterId='tradeshows'
            organisationId={organisation.id}
            setFilterGroup={setWorkItemCategoryFilterGroup}
            getFilter={getWorkItemCategoryFilter}
            disabled={isLoading}
          >
            Tradeshows
          </OrgFilterField>
          <OrgFilterField
            helpText='Publications'
            icon='book'
            filterType='category'
            filterId='publications'
            organisationId={organisation.id}
            setFilterGroup={setWorkItemCategoryFilterGroup}
            getFilter={getWorkItemCategoryFilter}
            disabled={isLoading}
          >
            Publications
          </OrgFilterField>
          <OrgFilterField
            helpText='Clinical Trials'
            icon='trial'
            filterType='category'
            filterId='clinicalTrials'
            organisationId={organisation.id}
            setFilterGroup={setWorkItemCategoryFilterGroup}
            getFilter={getWorkItemCategoryFilter}
            disabled={isLoading}
          >
            Clinical Trials
          </OrgFilterField>
          {hasIndustryLicense && (
            <>
              <OrgFilterField
                helpText='3rd Party Data'
                icon='magnifyingGlass'
                filterType='category'
                filterId='profile'
                organisationId={organisation.id}
                setFilterGroup={setWorkItemCategoryFilterGroup}
                getFilter={getWorkItemCategoryFilter}
                disabled={isLoading}
              >
                3rd Party Data
              </OrgFilterField>
            </>
          )}
        </CategoryCheckboxes>
        {!!showBuilder && (
          <SearchBuilder
            onSearch={handleSearchBuilderSearch}
            showAdvancedFields={false}
            id={organisation.id}
            saveInStore={false}
            onClear={handleSearchBuilderClear}
            filterId='term'
            filterGroup={filterGroup}
          />
        )}
      </SearchAreaContainer>
    )
  );
};

OrgSearchArea.propTypes = {
  organisation: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  handleSearch: PropTypes.func.isRequired,
  handleCancel: PropTypes.func,
  loading: PropTypes.bool,
  loadingSelector: PropTypes.func.isRequired,
  filterGroup: PropTypes.string,
  location: PropTypes.string.isRequired,
};
